import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState } from 'react';
import { useIntl } from 'react-intl-next';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx, css } from '@emotion/react';
import { useThemeObserver } from '@atlaskit/tokens';
import { Tab, TabList } from '@atlaskit/tabs';
import { Box, Flex, xcss, Stack, Inline } from '@atlaskit/primitives';
import { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import { useModal } from '@atlaskit/modal-dialog';
import Tooltip from '@atlaskit/tooltip';
import ChevronLeftIcon from '@atlaskit/icon/utility/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import CloseIcon from '@atlaskit/icon/core/close';
import { ResourcedEmoji } from '@atlaskit/emoji/element';
import { messages } from '../../shared/i18n';
import { Counter } from '../Counter';
var REACTIONS_CONTAINER_WIDTH = 56;
var REACTION_CONTAINER_HEIGHT = 40;
var containerEdgeAngle = {
  rightEdge: 270,
  leftEdge: 90
};
var leftNavigationStyle = xcss({
  marginLeft: 'space.200',
  alignSelf: 'self-start',
  marginTop: 'space.050'
});
var rightNavigationStyle = xcss({
  marginRight: 'space.100',
  marginLeft: 'auto',
  alignSelf: 'self-start',
  marginTop: 'space.050'
});
var fadedCss = function fadedCss(edge, theme) {
  return css({
    content: '""',
    position: 'absolute',
    left: '0px',
    top: '0px',
    width: "".concat(REACTIONS_CONTAINER_WIDTH, "px"),
    height: "".concat(REACTION_CONTAINER_HEIGHT, "px"),
    zIndex: 0,
    background:
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
    theme === 'dark' ? "linear-gradient(".concat(containerEdgeAngle[edge], "deg, rgba(34, 39, 43, 0.95) 40.23%, rgba(34, 39, 43, 0.55) 58.33%, rgba(34, 39, 43, 0) 77.49%)") : "linear-gradient(".concat(containerEdgeAngle[edge], "deg, rgba(255, 255, 255, 0.95) 40.23%, rgba(255, 255, 255, 0.55) 58.33%, rgba(255, 255, 255, 0) 77.49%)")
  });
};
var customTabListStyles = css({
  overflow: 'auto',
  display: 'flex',
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  'div[role=tablist]': {
    flexGrow: 1,
    // paddingInline exists to maintain styling prior to @atlaskit/tabs update that removed baked in horizontal padding
    paddingInline: "var(--ds-space-100, 8px)"
  },
  width: ' 100%',
  alignItems: 'flex-start'
});
var customTabWrapper = function customTabWrapper(theme) {
  return css({
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: "".concat(REACTIONS_CONTAINER_WIDTH, "px"),
    boxSizing: 'border-box',
    position: 'relative',
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
    '> div': {
      minWidth: "".concat(REACTIONS_CONTAINER_WIDTH, "px"),
      minHeight: "".concat(REACTION_CONTAINER_HEIGHT, "px"),
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
      padding: '0px !important',
      alignItems: 'center',
      justifyContent: 'center'
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
    '& > span': {
      minHeight: '16px',
      minWidth: '16px'
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
    '&.disabled:after': fadedCss('rightEdge', theme),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
    '&.disabled + &.disabled:after': fadedCss('leftEdge', theme)
  });
};
var firstElement = css({
  paddingLeft: "var(--ds-space-200, 16px)"
});
var fullWidthStyle = xcss({
  width: '100%',
  padding: 'space.300',
  paddingBlockEnd: 'space.400'
});
var emojiStyles = xcss({
  transformOrigin: 'center center 0',
  paddingInlineStart: 'space.100',
  paddingBlock: 'space.050',
  paddingInlineEnd: 'space.050'
});
var counterStyle = xcss({
  marginTop: 'space.025'
});
var CloseButton = function CloseButton(_ref) {
  var handleCloseReactionsDialog = _ref.handleCloseReactionsDialog;
  var intl = useIntl();
  return jsx(IconButton, {
    onClick: handleCloseReactionsDialog,
    icon: CloseIcon,
    label: intl.formatMessage(messages.closeReactionsDialog),
    appearance: "subtle",
    isTooltipDisabled: false
  });
};
var LeftNavigationButton = function LeftNavigationButton(_ref2) {
  var handlePreviousPage = _ref2.handlePreviousPage;
  var intl = useIntl();
  return jsx(Flex, {
    xcss: leftNavigationStyle
  }, jsx(IconButton, {
    spacing: "compact",
    onClick: handlePreviousPage,
    icon: ChevronLeftIcon,
    label: intl.formatMessage(messages.leftNavigateLabel),
    isTooltipDisabled: false
  }));
};
var RightNavigationButton = function RightNavigationButton(_ref3) {
  var handleNextPage = _ref3.handleNextPage;
  var intl = useIntl();
  return jsx(Flex, {
    xcss: rightNavigationStyle
  }, jsx(IconButton, {
    spacing: "compact",
    onClick: handleNextPage,
    icon: ChevronRightIcon,
    label: intl.formatMessage(messages.rightNavigateLabel),
    isTooltipDisabled: false
  }));
};
export var ReactionsDialogHeader = function ReactionsDialogHeader(_ref4) {
  var totalReactionsCount = _ref4.totalReactionsCount,
    handlePreviousPage = _ref4.handlePreviousPage,
    handleNextPage = _ref4.handleNextPage,
    currentPage = _ref4.currentPage,
    maxPages = _ref4.maxPages,
    currentReactions = _ref4.currentReactions,
    emojiProvider = _ref4.emojiProvider,
    handleCloseReactionsDialog = _ref4.handleCloseReactionsDialog;
  var _useState = useState({}),
    _useState2 = _slicedToArray(_useState, 2),
    cache = _useState2[0],
    setCache = _useState2[1];
  var _useModal = useModal(),
    titleId = _useModal.titleId;
  var intl = useIntl();
  var _useThemeObserver = useThemeObserver(),
    colorMode = _useThemeObserver.colorMode;
  var isSinglePage = maxPages === 1;
  var isOnFirstPage = currentPage === 1;
  var isOnLastPage = currentPage === maxPages;
  var handleHover = function handleHover(reaction) {
    var emojiId = reaction.emojiId;
    if (!emojiId || cache[emojiId]) {
      return;
    }
    _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var provider, emoji, capitalizedName;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return emojiProvider;
          case 2:
            provider = _context.sent;
            _context.next = 5;
            return provider.findByEmojiId({
              shortName: '',
              id: emojiId
            });
          case 5:
            emoji = _context.sent;
            if (emoji !== null && emoji !== void 0 && emoji.name) {
              // capitalize first letter of each string
              capitalizedName = emoji.name.replace(/\b\w/g, function (char) {
                return char.toUpperCase();
              });
              setCache(function (prevCache) {
                return _objectSpread(_objectSpread({}, prevCache), {}, _defineProperty({}, emojiId, capitalizedName));
              });
            }
          case 7:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }))();
  };
  return jsx(Stack, null, jsx(Flex, {
    direction: "row",
    justifyContent: "space-between",
    alignItems: "center",
    xcss: fullWidthStyle
  }, jsx(Heading, {
    size: "medium",
    id: titleId
  }, intl.formatMessage(messages.reactionsCount, {
    count: totalReactionsCount
  })), jsx(CloseButton, {
    handleCloseReactionsDialog: handleCloseReactionsDialog
  })), jsx(Inline, null, jsx("div", {
    css: customTabListStyles,
    id: "reactions-dialog-tabs-list"
  }, jsx(TabList, null, !isSinglePage && !isOnFirstPage && jsx(LeftNavigationButton, {
    handlePreviousPage: handlePreviousPage
  }), currentReactions.map(function (reaction, index) {
    var emojiId = {
      id: reaction.emojiId,
      shortName: ''
    };
    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      jsx("div", {
        // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
        css: [customTabWrapper(colorMode), index === 0 ? firstElement : []]
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
        ,
        className: "reaction-elements",
        key: reaction.emojiId,
        "data-testid": emojiId === null || emojiId === void 0 ? void 0 : emojiId.id,
        onMouseEnter: function onMouseEnter() {
          return handleHover(reaction);
        }
      }, jsx(Tab, null, jsx(Tooltip, {
        content: cache[reaction.emojiId],
        canAppear: function canAppear() {
          return !!cache[reaction.emojiId];
        }
      }, jsx(Flex, {
        justifyContent: "center",
        alignItems: "center",
        direction: "row"
      }, jsx(Box, {
        xcss: emojiStyles
      }, jsx(ResourcedEmoji, {
        emojiProvider: emojiProvider,
        emojiId: emojiId,
        fitToHeight: 16,
        showTooltip: true
      })), jsx(Box, {
        xcss: counterStyle
      }, jsx(Counter, {
        value: reaction.count
      }))))))
    );
  }), !isSinglePage && !isOnLastPage && jsx(RightNavigationButton, {
    handleNextPage: handleNextPage
  })))));
};